import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  occupantsOf,
  stayOf,
} from 'uplisting-frontend/services/calendar/-internals/occupancy';
import type { PropertyRowDataItem } from 'uplisting-frontend/services/calendar/-internals/types';
import BookingModel from 'uplisting-frontend/models/booking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export interface IUnitMove {
  booking: ModelRegistry['booking'];
  from: ModelRegistry['multi-unit'];
  to: ModelRegistry['multi-unit'];
  occupants: PropertyRowDataItem[];
  swapWith?: ModelRegistry['booking'];
}

export default class CalendarUnitMoveService extends Service {
  @service notifications!: Services['notifications'];
  @service('calendar/data') calendarData!: Services['calendar/data'];

  @service('repositories/booking')
  bookingRepository!: Services['repositories/booking'];

  @tracked pending?: IUnitMove;
  @tracked isSwapping = false;

  #answer?: (moveAnyway: boolean) => void;

  public movesFor(booking: ModelRegistry['booking']): IUnitMove[] {
    const { multiUnit: from } = booking;

    if (!from) {
      return [];
    }

    return booking.property.multiUnits
      .filter((unit) => unit !== from)
      .map((unit) => this.moveFor(booking, unit));
  }

  public moveFor(
    booking: ModelRegistry['booking'],
    to: ModelRegistry['multi-unit'],
  ): IUnitMove {
    const from = booking.multiUnit as ModelRegistry['multi-unit'];
    const stay = stayOf(booking);
    const occupants = stay ? occupantsOf(to, ...stay, [booking]) : [];

    return {
      booking,
      from,
      to,
      occupants,
      swapWith: this.#swapPartner(booking, from, occupants),
    };
  }

  public confirm(move: IUnitMove): Promise<boolean> {
    this.#answer?.(false);

    this.pending = move;

    return new Promise((resolve) => {
      this.#answer = resolve;
    });
  }

  public answer(moveAnyway: boolean): void {
    const answer = this.#answer;

    this.pending = undefined;
    this.#answer = undefined;

    answer?.(moveAnyway);
  }

  public async swap({
    booking,
    from,
    to,
    swapWith,
  }: IUnitMove): Promise<boolean> {
    if (!swapWith) {
      return false;
    }

    this.isSwapping = true;

    try {
      try {
        await this.saveUnit(booking, to);
      } catch {
        this.notifications.error(
          'calendar.unit_move.notifications.swap_failed',
        );

        return false;
      }

      try {
        await this.#saveRetryingOnce(swapWith, from);
      } catch {
        this.notifications.error(
          'calendar.unit_move.notifications.swap_half_done',
          false,
          { guest: swapWith.displayGuestName, unit: from.name },
        );

        return true;
      }

      this.notifications.info('calendar.unit_move.notifications.swapped', {
        guest: booking.displayGuestName,
        otherGuest: swapWith.displayGuestName,
      });

      return false;
    } finally {
      this.isSwapping = false;
      this.calendarData.setupFasterLookup();
    }
  }

  #swapPartner(
    booking: ModelRegistry['booking'],
    from: ModelRegistry['multi-unit'],
    occupants: PropertyRowDataItem[],
  ): ModelRegistry['booking'] | undefined {
    const [occupant] = occupants;

    if (
      occupants.length !== 1 ||
      !(occupant instanceof BookingModel) ||
      !this.calendarData.recordFor(occupant).meta.isDraggable
    ) {
      return;
    }

    const stay = stayOf(occupant);

    if (!stay) {
      return;
    }

    const blockers = occupantsOf(from, ...stay, [booking, occupant]);

    return blockers.length === 0 ? occupant : undefined;
  }

  async #saveRetryingOnce(
    booking: ModelRegistry['booking'],
    to: ModelRegistry['multi-unit'],
  ): Promise<void> {
    try {
      await this.saveUnit(booking, to);
    } catch {
      await this.saveUnit(booking, to);
    }
  }

  public async saveUnit(
    booking: ModelRegistry['booking'],
    to: ModelRegistry['multi-unit'],
  ): Promise<void> {
    booking.newMultiUnit = to;

    try {
      await this.bookingRepository.save(booking);
    } finally {
      booking.newMultiUnit = undefined;
    }

    booking.multiUnit = to;
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/unit-move': CalendarUnitMoveService;
  }
}
